import {
  ApiError,
  ApiErrorBody,
  BINARY,
  buildPath,
  buildQuery,
  errorCodeForStatus,
  isErrorCode,
  type AnyEndpoint,
  type EndpointInput,
  type EndpointOutput,
} from '@sd/contracts';
import type { Transport } from './platform';

/** The request never reached the server or the connection dropped. Safe to retry idempotent calls. */
export class NetworkError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'NetworkError';
  }
}

/** The server answered with something that does not match the contract. Never retried blindly. */
export class ContractError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'ContractError';
  }
}

export interface CallOptions {
  signal?: AbortSignal;
}

export interface UploadOptions extends CallOptions {
  onProgress?: (sentBytes: number, totalBytes: number) => void;
}

export interface ApiClient {
  readonly baseUrl: string;
  call<E extends AnyEndpoint>(endpoint: E, input?: EndpointInput<E>, options?: CallOptions): Promise<EndpointOutput<E>>;
  /** Raw-body upload with progress (XMLHttpRequest; fetch has no upload progress). */
  upload<E extends AnyEndpoint>(endpoint: E, input: EndpointInput<E>, options?: UploadOptions): Promise<EndpointOutput<E>>;
  /** Absolute URL for an endpoint (used for browser downloads and previews). */
  url<E extends AnyEndpoint>(endpoint: E, input?: Pick<EndpointInput<E>, 'params' | 'query'>): string;
}

export interface ApiClientOptions extends Transport {
  fetch?: typeof fetch;
  /** Reads the CSRF cookie (web). Defaults to `document.cookie`. */
  readCsrf?: () => string | null;
  createXhr?: () => XMLHttpRequest;
}

const CSRF_HEADER = 'x-sd-csrf';

/** The CSRF cookie: `__Host-sd_csrf` on https, `sd_csrf` on a local http development server. */
export function readCsrfCookie(cookie: string | undefined = globalThis.document?.cookie): string | null {
  if (!cookie) return null;
  const values = new Map<string, string>();
  for (const part of cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name && !values.has(name)) values.set(name, rest.join('='));
  }
  const value = values.get('__Host-sd_csrf') ?? values.get('sd_csrf');
  return value === undefined ? null : decodeURIComponent(value);
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const readCsrf = options.readCsrf ?? (() => readCsrfCookie());

  function urlFor(endpoint: AnyEndpoint, input?: { params?: unknown; query?: unknown }): string {
    const path = buildPath(endpoint.path, input?.params as Record<string, string> | undefined);
    return `${baseUrl}${path}${buildQuery(input?.query as Record<string, unknown> | undefined)}`;
  }

  async function headersFor(endpoint: AnyEndpoint, forceRefresh: boolean): Promise<Record<string, string>> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (endpoint.auth === 'public' && endpoint.method === 'GET') return headers;
    if (options.auth.kind === 'bearer') {
      const token = await options.auth.getToken({ forceRefresh });
      if (token) headers.authorization = `Bearer ${token}`;
    } else if (endpoint.method !== 'GET') {
      const csrf = readCsrf();
      if (csrf) headers[CSRF_HEADER] = csrf;
    }
    return headers;
  }

  function encodeBody(endpoint: AnyEndpoint, body: unknown): { body?: BodyInit; contentType?: string } {
    if (body === undefined) return {};
    if (endpoint.rawBody) return { body: body as BodyInit, contentType: 'application/octet-stream' };
    const parsed = endpoint.body.safeParse(body);
    if (!parsed.success) throw new ContractError(`request for ${endpoint.id} does not match the contract: ${parsed.error.message}`);
    return { body: JSON.stringify(parsed.data), contentType: 'application/json' };
  }

  async function parseSuccess(endpoint: AnyEndpoint, response: Response): Promise<unknown> {
    if (endpoint.response === BINARY) return response;
    if (response.status === 204) return undefined;
    let json: unknown;
    try {
      json = await response.json();
    } catch (cause) {
      throw new ContractError(`invalid JSON from ${endpoint.id}`, { cause });
    }
    const parsed = endpoint.response.safeParse(json);
    if (!parsed.success) throw new ContractError(`response of ${endpoint.id} does not match the contract: ${parsed.error.message}`);
    return parsed.data;
  }

  async function toApiError(response: Response): Promise<ApiError> {
    const requestId = response.headers.get('x-request-id') ?? undefined;
    try {
      const parsed = ApiErrorBody.safeParse(await response.json());
      if (parsed.success) {
        const { code, message, details } = parsed.data.error;
        return new ApiError(isErrorCode(code) ? code : errorCodeForStatus(response.status), message, details, parsed.data.error.requestId ?? requestId);
      }
    } catch {
      // fall through to a status-based error
    }
    return new ApiError(errorCodeForStatus(response.status), `HTTP ${response.status}`, undefined, requestId);
  }

  async function call<E extends AnyEndpoint>(endpoint: E, input?: EndpointInput<E>, callOptions?: CallOptions): Promise<EndpointOutput<E>> {
    const url = urlFor(endpoint, input);
    const { body, contentType } = encodeBody(endpoint, input?.body);
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const headers = await headersFor(endpoint, attempt > 0);
      if (contentType) headers['content-type'] = contentType;
      let response: Response;
      try {
        response = await doFetch(url, {
          method: endpoint.method,
          headers,
          body,
          credentials: options.auth.kind === 'cookie' ? 'same-origin' : 'omit',
          // Never follow redirects with credentials to another origin.
          redirect: 'error',
          signal: callOptions?.signal,
        });
      } catch (cause) {
        if (callOptions?.signal?.aborted) throw cause;
        throw new NetworkError(`network error calling ${endpoint.id}`, { cause });
      }
      if (response.ok) return (await parseSuccess(endpoint, response)) as EndpointOutput<E>;
      const error = await toApiError(response);
      // One silent retry with a fresh token when a desktop access token expired.
      if (attempt === 0 && options.auth.kind === 'bearer' && error.code === 'TOKEN_EXPIRED') continue;
      throw error;
    }
    throw new ApiError('UNAUTHENTICATED', 'token refresh failed');
  }

  function upload<E extends AnyEndpoint>(endpoint: E, input: EndpointInput<E>, uploadOptions?: UploadOptions): Promise<EndpointOutput<E>> {
    const url = urlFor(endpoint, input);
    const body = input.body as Blob | ArrayBuffer | Uint8Array;
    const total = body instanceof Blob ? body.size : body.byteLength;
    return headersFor(endpoint, false).then(
      (headers) =>
        new Promise<EndpointOutput<E>>((resolve, reject) => {
          // Aborted before the request started: the abort event has already fired, so it would never cancel the XHR.
          if (uploadOptions?.signal?.aborted) {
            reject(new DOMException('upload aborted', 'AbortError'));
            return;
          }
          const xhr = options.createXhr?.() ?? new XMLHttpRequest();
          xhr.open(endpoint.method, url);
          xhr.withCredentials = false;
          for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
          xhr.setRequestHeader('content-type', 'application/octet-stream');
          xhr.responseType = 'text';
          xhr.upload.onprogress = (event) => uploadOptions?.onProgress?.(event.loaded, event.lengthComputable ? event.total : total);
          xhr.onerror = () => reject(new NetworkError(`network error uploading via ${endpoint.id}`));
          xhr.onabort = () => reject(new DOMException('upload aborted', 'AbortError'));
          xhr.onload = () => {
            const response = new Response(xhr.status === 204 ? null : xhr.responseText, {
              status: xhr.status,
              headers: { 'content-type': xhr.getResponseHeader('content-type') ?? 'application/json' },
            });
            if (response.ok) parseSuccess(endpoint, response).then((value) => resolve(value as EndpointOutput<E>), reject);
            else toApiError(response).then(reject, reject);
          };
          uploadOptions?.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
          xhr.send(body as XMLHttpRequestBodyInit);
        }),
    );
  }

  return { baseUrl, call, upload, url: (endpoint, input) => urlFor(endpoint, input) };
}
